/*

  BlankDialog.ts

  Blank tile dialog component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IBaseGame } from "./types";
import { ComponentFunc, m, VnodeChildren } from "./mithril";
import { buttonOut, buttonOver, glyph } from "./util";
import { mt, ts } from "./i18n";
import { DialogButton } from "./components";

const BLANK_TILES_PER_LINE = 6;

interface IAttributes {
  game: IBaseGame;
}  

export const BlankDialog: ComponentFunc<IAttributes> = () => {
  // A dialog for choosing the meaning of a blank tile

  function blankLetters(game: IBaseGame): VnodeChildren {
    let legalLetters = game.alphabet;
    let len = legalLetters.length;
    let ix = 0;
    let r: VnodeChildren = [];

    while (len > 0) {
      /* Rows */
      let c = [];
      /* Columns: max BLANK_TILES_PER_LINE tiles per row */
      for (let i = 0; i < BLANK_TILES_PER_LINE && len > 0; i++) {
        let letter = legalLetters[ix++];
        c.push(
          m("td",
            {
              onclick: (ev: Event) => { game.placeBlank(letter); ev.preventDefault(); },
              onmouseover: buttonOver,
              onmouseout: buttonOut
            },
            m(".blank-choice.netskrafl-tile.netskrafl-racktile", letter)
          )
        );
        len--;
      }
      r.push(m("tr", c));
    }
    return r;
  }

  return {
    view: (vnode) => {
      const { game } = vnode.attrs;
      if (!game) return undefined;
      return m(".modal-dialog",
        {
          id: 'blank-dialog',
          style: { visibility: "visible" }
        },
        m(".ui-widget.ui-widget-content.ui-corner-all", { id: 'blank-form' },
          [
            mt("p", "Hvaða staf táknar auða flísin?"),
            m(".rack.blank-rack",
              m("table.board", { id: 'blank-meaning' }, blankLetters(game))
            ),
            m(DialogButton,
              {
                id: 'blank-close',
                title: ts("Hætta við"),
                onclick: (ev: Event) => {
                  ev.preventDefault();
                  game.cancelBlankDialog();
                }
              },
              glyph("remove")
            )
          ]
        )
      );
    }
  };
};
